import {
  Button,
  chakra,
  Divider,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast
} from "@chakra-ui/react";
import { FormDatepicker, FormSelect, FormTextArea } from "../../../../../components/Forms";
import {
  generateArrayOfYears,
  SUPPLIER_QUESTIONNAIRE_TYPES,
  showErrorToast,
  sortAlphabeticallyByAttribute
} from "../../../../../utils";
import { Controller, useForm } from "react-hook-form";
import { useGetOrganizationStructure, useGetAllSuppliers, useGetSupplierUsers } from "../../../../../hooks/query";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { mapFormDataToServerData, mapServerDataToFormData } from "./utils";
import { useDispatch } from "react-redux";
import { hideAppSpinner, showAppSpinner } from "../../../../../context/Store/slices/appSpinnerSlice";
import * as React from "react";
import moment from "moment/moment";
import { sortUsersByEmail } from "../../../../../utils/userSort";

export const ALL_SUPPLIERS_VALUE = "ALL";

function SuppliersPlanningModal({ modalData, isOpen, onClose, onSubmit }) {
  const toast = useToast();
  const dispatch = useDispatch();
  const { t: globalTranslation } = useTranslation("translation");
  const { t } = useTranslation("translation", {
    keyPrefix: "dataEntryPlanning"
  });
  const [suppliers, setSuppliers] = useState([]);

  const {
    data: organizationStructure
  } = useGetOrganizationStructure();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    resetField,
    control
  } = useForm({
    defaultValues: mapServerDataToFormData(modalData, organizationStructure?.consumptionBasedEnabled ?? false)
  });

  const selectedSupplier = watch("supplier", "");
  const [isInitial, setIsInitial] = useState(true);

  const {
    isLoading: isLoadingSuppliers
  } = useGetAllSuppliers({
    onSuccess: (data) => {
      setSuppliers(data.sort((a, b) => sortAlphabeticallyByAttribute(a, b, "name")))
    },
    onError: err => showErrorToast(toast, err.message),
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0
  });

  const {
    data: users = [],
    isLoading: isLoadingUsers
  } = useGetSupplierUsers(selectedSupplier, {
    enabled: selectedSupplier !== "" && selectedSupplier !== ALL_SUPPLIERS_VALUE,
    onSuccess: () => {
      if (isInitial && modalData) {
        // Used only in 'Edit supplier task' case. Once the users of the selected supplier are loaded as options,
        // the form is reset, so the selected assignee (if exists) can be shown.
        setIsInitial(false);
        reset();
      }
    },
    onError: err => showErrorToast(toast, err.message),
    cacheTime: 0
  });

  const prepareSubmitData = data => {
    if (!modalData && data.supplier === ALL_SUPPLIERS_VALUE) {
      const dataToSubmit = {
        description: data.description,
        period: {
          year: data.year,
          quarter: data.quarter
        },
        questionnaireType: data.questionnaireType
      };
      if (data.endDate !== null && typeof data.endDate !== "undefined" && data.endDate !== "") {
        dataToSubmit.endDate = moment(data.endDate).format("YYYY-MM-DD");
      }
      onSubmit(modalData?.id, dataToSubmit);
    } else {
      onSubmit(modalData?.id, mapFormDataToServerData(data, modalData));
    }
  };

  const onModalClose = () => {
    onClose();
  };

  useEffect(() => {
    reset(mapServerDataToFormData(modalData, organizationStructure?.consumptionBasedEnabled ?? false));
  }, [reset, modalData, isOpen, organizationStructure]);

  useEffect(() => {
    if (isLoadingSuppliers || isLoadingUsers) {
      dispatch(showAppSpinner());
    } else {
      dispatch(hideAppSpinner());
    }
  }, [dispatch, isLoadingSuppliers, isLoadingUsers]);

  return !isLoadingSuppliers && isOpen && organizationStructure ? <Modal isOpen={isOpen} onClose={onModalClose} size="lg">
    <ModalOverlay />
    <ModalContent>
      <chakra.form onSubmit={handleSubmit(prepareSubmitData)}>
        <ModalHeader>{modalData ? t("modal.editTaskHeader") : t("modal.createTaskHeader")}</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Heading size="md" marginBlockEnd="2">{t("modal.generalData")}</Heading>
          <Flex direction="column" gap="2">
            <FormTextArea
              label={t("modal.description")}
              type="text"
              errors={errors}
              {...register("description")}
            />
            <FormSelect
              label={`${t("modal.supplier")} *`}
              errors={errors}
              {...register("supplier",
                { required: globalTranslation("entityRequired", { entityName: t("modal.supplier") }) })}
              onChange={(e) => {
                resetField("supplier", { defaultValue: e.target.value });
                setValue("assignee", "");
              }}
            >
              {!modalData && <Fragment>
                <option value={ALL_SUPPLIERS_VALUE}>{ALL_SUPPLIERS_VALUE}</option>
                <Divider />
              </Fragment>}
              {suppliers.map(supplier =>
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              )}
            </FormSelect>
            {!modalData &&
              <FormSelect
                label={`${t("modal.questionnaireType")} *`}
                errors={errors}
                {...register("questionnaireType",
                  { required: globalTranslation("entityRequired", { entityName: t("modal.questionnaireType") }) })}
              >
                <option value={SUPPLIER_QUESTIONNAIRE_TYPES.EMISSION_BASED}>
                  {t(`modal.supplierQuestionnaireTypes.${SUPPLIER_QUESTIONNAIRE_TYPES.EMISSION_BASED}`)}
                </option>
                <option
                  disabled={!organizationStructure.consumptionBasedEnabled}
                  value={SUPPLIER_QUESTIONNAIRE_TYPES.CONSUMPTION_BASED}
                  style={{ backgroundColor: !organizationStructure.consumptionBasedEnabled ? "lightgray" : null }}
                >
                  {t(`modal.supplierQuestionnaireTypes.${SUPPLIER_QUESTIONNAIRE_TYPES.CONSUMPTION_BASED}`)}
                </option>
                <option value={SUPPLIER_QUESTIONNAIRE_TYPES.DECARBONIZATION_NET_ZERO_PLAN}>
                  {t(`modal.supplierQuestionnaireTypes.${SUPPLIER_QUESTIONNAIRE_TYPES.DECARBONIZATION_NET_ZERO_PLAN}`)}
                </option>
              </FormSelect>
            }
            {selectedSupplier !== ALL_SUPPLIERS_VALUE &&
              <Fragment>
                <FormSelect
                  label={`${t("modal.assignee")} *`}
                  placeholder=" "
                  errors={errors}
                  {...register("assignee",
                    { required: globalTranslation("entityRequired", { entityName: t("modal.assignee") }) })}
                >
                  {sortUsersByEmail(users).map(assignee =>
                    <option key={assignee.id} value={assignee.id}>
                      {assignee.email}
                    </option>
                  )}
                </FormSelect>
              </Fragment>
            }
            <Controller
              control={control}
              name={"endDate"}
              render={({ field }) => {
                const props = {};
                if (field.value !== null && field.value !== "") {
                  props.date = field.value;
                }
                return <FormDatepicker
                  field={field}
                  name={"endDate"}
                  label={t("modal.endDate")}
                  usePortal={false}
                  minDate={new Date(moment().subtract(1, 'days'))}
                  {...props}
                />;
              }}
            />
          </Flex>

          <Heading size="md" marginBlockStart="10" marginBlockEnd="2">{t("modal.selectPeriod")}</Heading>
          <Flex direction="column" gap="2">
            <FormSelect
              label={`${t("modal.financialYear")} *`}
              type="text"
              errors={errors}
              {...register("year",
                { required: globalTranslation("entityRequired", { entityName: t("modal.financialYear") }) })}
            >
              {generateArrayOfYears().map(year => <option key={year} value={year}>{year}</option>)}
            </FormSelect>
            <FormSelect
              label={`${t("modal.quarter")} *`}
              type="text"
              errors={errors}
              {...register("quarter",
                { required: globalTranslation("entityRequired", { entityName: t("modal.quarter") }) })}
            >
              <option key="ALL" value="ALL">{t("modal.quarterAll")}</option>
              <option key="Q1" value="Q1">{`${t("modal.quarterLetter")}1`}</option>
              <option key="Q2" value="Q2">{`${t("modal.quarterLetter")}2`}</option>
              <option key="Q3" value="Q3">{`${t("modal.quarterLetter")}3`}</option>
              <option key="Q4" value="Q4">{`${t("modal.quarterLetter")}4`}</option>
            </FormSelect>
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onModalClose}>
            {t("modal.close")}
          </Button>
          <Button colorScheme="customScheme" type="submit">
            {t("modal.save")}
          </Button>
        </ModalFooter>
      </chakra.form>
    </ModalContent>
  </Modal> : null;
}

export default SuppliersPlanningModal;